import { Context, Dispatch, useContext } from "react";
import {
  FilterKeys,
  QueryFilter,
  QueryFilterEvent,
} from "../../../interfaces/filters";
import {
  CFChangeEvent,
  CustomFilterConfig,
  CustomFilterUnion,
  TCustomFilter,
} from "./types";
import CustomFilterItem from "./CustomFilterItem";
import { random, sortBy } from "lodash";
import SuperDropdown from "../../dropdown/SuperDropdown";
import CustomFilterDropdownListItem from "../../dropdown/CustomFilterDropdownListItem";

export default function CustomFilter<
  C extends CustomFilterUnion,
  Keys extends FilterKeys,
>(props: {
  filterConfig: CustomFilterConfig<C>[];
  queryFilterContext: Context<QueryFilter<Keys>[]>;
  queryFilterEventContext: Context<Dispatch<QueryFilterEvent<Keys>>>;
}) {
  // deeestruction!
  const { filterConfig, queryFilterContext, queryFilterEventContext } = props;

  // in context
  const queryFilters = useContext(queryFilterContext);
  const updateQueryFilters = useContext(queryFilterEventContext);

  // haul out the settings
  const customFilters: TCustomFilter[] =
    queryFilters.find((qf) => qf.filterKey === "custom")?.settings || [];

  // sort the config
  const sortedFilterConfig = sortBy(filterConfig, ["category", "displayName"]);

  // handlers
  function addFilterHandler(id: string, configKey: string) {
    const updatedFilters: TCustomFilter[] = [
      ...customFilters,
      {
        id: id,
        configKey: configKey,
      },
    ];

    const queryFilter: QueryFilter = {
      filterKey: "custom",
      parentFilter: "and",
      filter: updatedFilters.map((cf) => cf.filterString).join("\n"),
      settings: updatedFilters,
    };

    updateQueryFilters({
      event: "update",
      queryFilter: queryFilter as QueryFilter<Keys>,
    });
  }

  function onChangeHandler(id: string, event: CFChangeEvent) {
    // update filter if id exists in custom filters
    for (const cf of customFilters) {
      if (cf.id === id) {
        cf.filterString = event.filterString;
        cf.filterObj = event.filterObj;
        cf.settings = event.filterSettings;
        break;
      }
    }

    const queryFilter: QueryFilter = {
      filterKey: "custom",
      parentFilter: "and",
      filter: customFilters.map((cf) => cf.filterString).join("\n"),
      settings: customFilters,
    };

    updateQueryFilters({
      event: "update",
      queryFilter: queryFilter as QueryFilter<Keys>,
    });
  }

  function onRemoveHandler(id: string) {
    const updatedFilters: TCustomFilter[] = customFilters.filter(
      (filter) => id !== filter.id,
    );

    const queryFilter: QueryFilter = {
      filterKey: "custom",
      parentFilter: "and",
      filter: updatedFilters.map((cf) => cf.filterString).join("\n"),
      settings: updatedFilters,
    };

    updateQueryFilters({
      event: "update",
      queryFilter: queryFilter as QueryFilter<Keys>,
    });
  }

  return (
    <div className="c-custom-filter">
      {customFilters.map((cf) => {
        return (
          <CustomFilterItem
            filterConfig={filterConfig.find((conf) => conf.id === cf.configKey)}
            onChangeFilter={(event) => {
              onChangeHandler(cf.id, event);
            }}
            onRemoveFilter={() => {
              onRemoveHandler(cf.id);
            }}
            settings={cf.settings}
            key={cf.id}
          />
        );
      })}

      <SuperDropdown
        itemsIdKey={"id"}
        items={sortedFilterConfig}
        filterKeys={["displayName"]}
        itemToString={(item) => item?.displayName ?? ""}
        placeholder="Tilføj filter ..."
        classNameInput={`p-1 dashed fs--accordion`}
        clearInputAfterSelection={true}
        onSelectedItemEvent={(event) => {
          if (event.selectedItem) {
            const id = random(0, 100000);
            addFilterHandler(String(id), event.selectedItem?.id);
          }
        }}
        itemToElement={(item) => (
          <CustomFilterDropdownListItem
            label={item.displayName}
            chipLabel={item.category}
          />
        )}
      />
    </div>
  );
}
